import React, { useEffect, useState, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
export type UserType = 'admin' | 'investor' | 'farmer' | null;
interface User {
  id: string;
  name: string;
  email: string;
  type: UserType;
  profileImage?: string;
}
interface UserContextType {
  user: User | null;
  userType: UserType;
  isAuthenticated: boolean;
  login: (email: string, password: string, userType: UserType) => Promise<void>;
  logout: () => void;
  setUserType: (type: UserType) => void;
}
const UserContext = createContext<UserContextType | undefined>(undefined);
export const UserProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const navigate = useNavigate();
  // Check for existing session on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('growconnect_user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      setIsAuthenticated(true);
    }
  }, []);
  const login = async (email: string, password: string, userType: UserType) => {
    // In a real app, this would make an API call to authenticate
    // For demo purposes, we'll just simulate a successful login
    // Create mock user based on type
    const mockUsers = {
      admin: {
        id: 'admin-1',
        name: 'Admin User',
        email: email,
        type: 'admin' as UserType,
        profileImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
      },
      investor: {
        id: 'investor-1',
        name: 'Investor User',
        email: email,
        type: 'investor' as UserType,
        profileImage: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
      },
      farmer: {
        id: 'farmer-1',
        name: 'Farmer User',
        email: email,
        type: 'farmer' as UserType,
        profileImage: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
      }
    };
    const newUser = mockUsers[userType as keyof typeof mockUsers];
    // Save to state and localStorage
    setUser(newUser);
    setIsAuthenticated(true);
    localStorage.setItem('growconnect_user', JSON.stringify(newUser));
    // Redirect to the appropriate dashboard
    navigate(`/${userType}/dashboard`);
  };
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('growconnect_user');
    navigate('/');
  };
  const setUserType = (type: UserType) => {
    if (user) {
      const updatedUser = {
        ...user,
        type
      };
      setUser(updatedUser);
      localStorage.setItem('growconnect_user', JSON.stringify(updatedUser));
    }
  };
  return <UserContext.Provider value={{
    user,
    userType: user?.type || null,
    isAuthenticated,
    login,
    logout,
    setUserType
  }} data-id="element-550">
      {children}
    </UserContext.Provider>;
};
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};